import { prettifySalesCategory } from "api-types/builders/salesCategory";
import type { BuilderSalesCategoryCustomization } from "api-types/builders/salesCategoryTypes";
import type { DocOption, DocOptionSet } from "building-types/options/optionSet";
import type { OptionOverrides, OptionSetOverride } from "building-types/options/optionTemplateTypes";
import { FALLBACK_VALUE } from "common/constants";
import { ensureDefined } from "common/utils/assertOfType";
import objectIsEmpty from "common/utils/objectIsEmpty";
import { camelCaseToTitleCase, snakeCaseToTitleCase } from "common/utils/strings";
import type { NestedPartial } from "common/utils/types";
import { PlanLibraryBadge } from "lib/components/builders/plan-library/PlanLibraryBadge";
import { planLibraryActions } from "lib/components/builders/plan-library/planLibraryReducer";
import { usePlanLibraryDispatch, usePlanLibrarySelector } from "lib/components/builders/plan-library/planLibraryStore";
import type { PlanLibraryOptionSet } from "lib/components/builders/plan-library/planLibraryTypes";
import type { ReactNode } from "react";
import Button from "ui/buttons/Button";
import Modal from "ui/components/Modal";
import { Message } from "ui/text/Message";
import styles from "./PlanLibraryModalWrapper.module.scss";

function getOptionSetPropValue(
  optionSet: NestedPartial<Omit<DocOptionSet, "options">> | null | undefined,
  key: string,
  salesCategoryCustomization: BuilderSalesCategoryCustomization | undefined,
) {
  const value = optionSet?.[key];
  switch (key) {
    case "disabled":
      return value?.toString() ?? "false";
    case "salesCategory":
      return prettifySalesCategory(value, salesCategoryCustomization);
    default:
      return value?.toString() ?? "";
  }
}

function getOptionPropValue(option: OptionOverrides | null | undefined, key: string) {
  const value = option?.[key];
  switch (key) {
    case "price":
      return value?.toString() ?? "0";
    case "disabled":
      return value?.toString() ?? "false";
    case "salesImages":
      return `Image count: ${value?.length ?? 0}`;
    default:
      return value?.toString() ?? "none";
  }
}

interface OptionOverrideDiscardListProps {
  inherited: DocOption | null | undefined;
  toDiscard: OptionOverrides | undefined;
}

function OptionOverrideDiscardList({ inherited, toDiscard }: OptionOverrideDiscardListProps) {
  const { modalOptionSet } = usePlanLibrarySelector((state) => ({ modalOptionSet: state.modalOptionSet }));

  if (!objectIsEmpty(toDiscard) && !objectIsEmpty(inherited)) {
    const option = modalOptionSet?.options?.find?.(({ id }) => id === inherited?.id);

    return (
      <div className={styles.optionSetOverridesToClearWrapper}>
        <span className={styles.optionNameWrapper}>{option?.name ?? FALLBACK_VALUE}</span>
        {Object.keys(toDiscard ?? {}).map((key) => {
          if (key !== "sortIndex") {
            return (
              <table key={key} className={styles.optionSetOverridesTable}>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Before</th>
                    <th>After</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{camelCaseToTitleCase(key)}</td>
                    <td className={styles.currentValueWrapper}>{getOptionPropValue(toDiscard, key)}</td>
                    <td>{getOptionPropValue(inherited, key)}</td>
                  </tr>
                </tbody>
              </table>
            );
          }
          return undefined;
        })}
      </div>
    );
  }

  return null;
}

interface OptionSetOverrideDiscardListProps {
  inherited: PlanLibraryOptionSet | null | undefined;
  toDiscard: OptionSetOverride;
}

function OptionSetOverrideDiscardList({ inherited, toDiscard }: OptionSetOverrideDiscardListProps) {
  const { modalOptionSet, salesCategoryCustomization } = usePlanLibrarySelector((state) => ({
    modalOptionSet: state.modalOptionSet,
    salesCategoryCustomization: state.builder.salesConfig?.salesCategoryCustomization,
  }));

  if (!objectIsEmpty(toDiscard) && modalOptionSet) {
    return (
      <div className={styles.optionSetOverridesToClearWrapper}>
        <h3 className={styles.optionSetNameWrapper}>{modalOptionSet.name}</h3>

        {Object.keys(toDiscard).map((key) => {
          if (key !== "options" && key !== "sortIndex") {
            return (
              <table key={key} className={styles.optionSetOverridesTable}>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Before</th>
                    <th>After</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{camelCaseToTitleCase(key)}</td>
                    <td className={styles.currentValueWrapper}>
                      {getOptionSetPropValue(toDiscard, key, salesCategoryCustomization)}
                    </td>
                    <td>{getOptionSetPropValue(inherited, key, salesCategoryCustomization)}</td>
                  </tr>
                </tbody>
              </table>
            );
          }
          return undefined;
        })}

        {toDiscard.options && !objectIsEmpty(toDiscard.options) && (
          <details open style={{ marginTop: "1.5em" }}>
            <summary>Option Overrides</summary>

            {Object.keys(toDiscard.options).map((id, i) => {
              const inheritedOption = inherited?.options.find((x) => x.id === id);
              const optionOverrides = ensureDefined(toDiscard.options)[id];
              const keys = Object.keys(optionOverrides ?? {});

              if (keys?.length > 1 || keys?.[0] !== "sortIndex") {
                return (
                  <div key={i} className={styles.nestOptionSetOverridesTable}>
                    <OptionOverrideDiscardList inherited={inheritedOption} toDiscard={optionOverrides} />
                  </div>
                );
              }
              return undefined;
            })}
          </details>
        )}
      </div>
    );
  }

  return null;
}

interface PlanLibraryModalWrapperProps {
  children: ReactNode;
  title: string;
  size?: "small" | "medium" | "large" | "full";
  inheritedOptionSet?: PlanLibraryOptionSet;
  submitEnabled: boolean;
  isBaseOption?: boolean;
  onSubmit: () => void;
}

export default function PlanLibraryModalWrapper({
  children,
  title,
  size = "medium",
  inheritedOptionSet,
  submitEnabled,
  isBaseOption,
  onSubmit,
}: PlanLibraryModalWrapperProps) {
  const { overridesToDiscard, planType } = usePlanLibrarySelector((state) => ({
    overridesToDiscard: state.overridesToDiscard,
    planType: state.plans.planType,
  }));
  const dispatch = usePlanLibraryDispatch();

  function Title() {
    return (
      <div className={styles.modalHeader}>
        <span>{title}</span>
        <div className={styles.planType}>{snakeCaseToTitleCase(planType)}</div>
        {isBaseOption && (
          <div className={styles.optionSetBadgeWrapper} style={{ marginLeft: "auto" }}>
            <PlanLibraryBadge isBaseOption />
          </div>
        )}
      </div>
    );
  }

  return (
    <Modal
      open
      title={<Title />}
      size={size}
      onClose={() => {
        dispatch(planLibraryActions.setOverridesToDiscard(null));
        dispatch(planLibraryActions.enableOverrideModal(null));
      }}
      variants={["footerButtons", "bodyPadding"]}
      footer={
        <>
          {" "}
          <Button
            type="button"
            btnSize="large"
            btnStyle="tertiary"
            onClick={() => {
              dispatch(planLibraryActions.setOverridesToDiscard(null));
              dispatch(planLibraryActions.enableOverrideModal(null));
            }}
          >
            Cancel
          </Button>
          <Button btnSize="large" type="submit" disabled={!submitEnabled} onClick={onSubmit}>
            {overridesToDiscard ? "Discard" : "Submit"}
          </Button>
        </>
      }
    >
      {overridesToDiscard ? ( // overridesToDiscard set when clicking on a discard override badge
        <>
          <Message messageType="warning" marginBottom>
            <>
              Are you sure you wish to discard the following overrides? <b>This action cannot be undone.</b>
            </>
          </Message>
          <OptionSetOverrideDiscardList inherited={inheritedOptionSet} toDiscard={overridesToDiscard} />
        </>
      ) : (
        children
      )}
    </Modal>
  );
}
